import { addItem } from "./add-item";
import { Logger } from "./logger";
import { removeItem } from "./remove-item";
import { spaceGenerator } from "./space-generator";

const fs = require('fs');

export interface FileWriterData {
    filePath: string;
}

export class FileWriter {
    private data: string;
    private filePath: string;
    private needImportHandler = false;

    /**
     * the getter data.
     * @returns the data.
     */
    public getData() {
        return this.data;
    }

    /**
     * the constructor.
     * @param path the path.
     */
    constructor(path: string) {
        Logger.log(path);
        this.data = this.readFile(path);
    }

    /**
     * the read file function.
     * @param filePath the file path.
     * @returns the file content.
     */
    public readFile(filePath) {
        this.filePath = filePath;
        return fs.readFileSync(filePath, 'utf8');
    }

    /**
     * the tab to two spaces function.
     * @returns this context.
     */
    public tabToTwoSpaces() {
        const lines = this.data.split('\n');

        if (lines.length > 1 && /^    /.test(lines[1])) {
            this.data = this.data.replace(/    /g, '  ');
        }

        return this;
    }

    /**
     * replaces the version in version.json
     * @param input the input.
     * @returns 
     */
    public replaceVersion(input: object) {
        const regexPattern = /"version":\s+"(\d+\.\d+\.\d+)"/g;
        
        for (const [_, newKeyword] of Object.entries(input)) {
            Logger.log(this.data)
            this.data = this.data.replace(regexPattern, newKeyword);
        }

        return this;
    }

    /**
     * the add content function.
     * the default indent count should be matching with addBefore or addAfter.
     * @param input the input.
     * @param wholeLine the whole line flag.
     * @param space the space number.
     * @param connector the connector.
     * @returns this context.
     */
    public addContent(input: object | addItem[], wholeLine = false, space = 4, connector=',') {
        if (typeof input === 'object' && !Array.isArray(input)) {
            for (const [oldKeyword, newKeyword] of Object.entries(input)) {
                if (!this.checkIfKeywordExists(newKeyword)) {
                    const regex = wholeLine ? new RegExp(`^${this.escapeRegExp(oldKeyword)}$`, 'gm') : new RegExp(this.escapeRegExp(oldKeyword), 'g');
                    this.data = this.data.replace(regex, `${oldKeyword}${connector}\n${spaceGenerator(space)}${newKeyword}`);
                }
            }
        } else {
            for(let i = 0; i < input.length; i++) {
                let item = input[i];
                if (!this.checkIfKeywordExists(item.toBeAdded) && !this.checkIfKeywordExists(item.keyword)) {
                    let indent = '';

                    if (item.needImportHandler && (
                        item.toBeAddedBefore && this.checkIfKeywordExists(item.toBeAddedBefore) || item.toBeAddedAfter && this.checkIfKeywordExists(item.toBeAddedAfter))) {
                        this.needImportHandler = true;
                    }

                    if (item.toBeAddedAfter) {
                        indent = spaceGenerator(this.getIndentByKeyword(this.data, item.toBeAddedAfter));
                        const regex = wholeLine ? new RegExp(`^${this.escapeRegExp(item.toBeAddedAfter)}$`, 'gm') : new RegExp(this.escapeRegExp(item.toBeAddedAfter), 'g');
                        this.data = this.data.replace(
                            regex,
                            `${item.toBeAddedAfter}${item.connector === undefined ? connector : item.connector}\n${indent}${item.toBeAdded}`);
                    } else if (item.toBeAddedBefore) {
                        if (item.toBeAddedBefore) {
                            indent = spaceGenerator(this.getIndentByKeyword(this.data, item.toBeAddedBefore));
                        }
                        const regex = wholeLine ? new RegExp(`^${this.escapeRegExp(item.toBeAddedBefore)}$`, 'gm') : new RegExp(this.escapeRegExp(item.toBeAddedBefore), 'g');
                        this.data = this.data.replace(
                            regex,
                            `${item.toBeAdded}\n${indent}${item.toBeAddedBefore}`);
                    }
                }
            }
        }

        return this;
    }

    /**
     * the fix imports function.
     * @returns this context.
     */
    public fixImportHandler(imports: string) {
        if (!this.needImportHandler) {
            return this;
        }

        this.needImportHandler = false;

        this.importFixer(imports);

        Logger.log(imports);

        return this;
    }

    /**
     * the replace dependency version function.
     * @param input the input.
     * @returns the this context.
     */
    public replaceDependencyVersion(input: object, ignore = false) {
        if (ignore) {
            return this;
        }

        for (const [oldKeyword, newKeyword] of Object.entries(input)) {
            const oldKeywordRegex = new RegExp(
                `"${this.escapeRegExp(oldKeyword.match(/"([^"]+)"/)[1])}": "\\d+\\.\\d+\\.\\d+"`,
                'g'
            );
            this.data = this.data.replace(oldKeywordRegex, newKeyword);

            const oldKeywordRegex2 = new RegExp(
                `"${this.escapeRegExp(oldKeyword.match(/"([^"]+)"/)[1])}": ["^~]+\\d+\\.\\d+\\.\\d+"`,
                'g'
            );
            this.data = this.data.replace(oldKeywordRegex2, newKeyword);
        }

        return this;
    }

    /**
     * the remove content function.
     * @param input the input array.
     * @returns the this context.
     */
    public removeContent(input: string[] | removeItem[]) {
        if (!input || !input.length) {
            return this;
        }

        if (typeof input[0] === 'string') {
            this.data = this.data.split('\n')
            .filter(line => {
                return !input.some(keyword => line.includes(keyword))
            })
            .join('\n');
        } else {
            let tempData = this.data.split('\n');
            for (let i = 0; i < input.length; i++) {
                if (!(<removeItem>input[i]).exactMatch) {
                    tempData = this.removeByKeyword(tempData, (<removeItem>input[i]).key, (<removeItem>input[i]).linesInTotalAfterKey, (<removeItem>input[i]).toBeRemovedAfter);
                } else {
                    tempData.splice(tempData.indexOf((<removeItem>input[i]).key), (<removeItem>input[i]).linesInTotalAfterKey);
                }
                this.data = tempData.join('\n');
            }
        }

        return this;
    }

    /**
     * the write file function.
     * @returns the write file result.
     */
    public writeFile() {
        fs.writeFileSync(this.filePath, this.data);
        return true;
    }

    /**
     * the replace content function.
     * @param input the input object
     * @returns the this context
     */
    public replaceContent(input: object) {
        for (const [oldKeyword, newKeyword] of Object.entries(input)) {
            const oldKeywordRegex = new RegExp(
                `${this.escapeRegExp(oldKeyword)}`,
                'gi'
            );
            this.data = this.data.replace(oldKeywordRegex, newKeyword);
        }

        return this;
    }

    /**
     * the replace tsconfig content function.
     * @param input the input object
     * @returns the this context
     */
    public replaceTsConfigContent(input: object) {
        for (const [oldKeyword, newKeyword] of Object.entries(input)) {
            const oldKeywordRegex = new RegExp(
                `"${this.escapeRegExp(oldKeyword.match(/"([^"]+)"/)[1])}":\\s*"[^"]+"`,
                'gi'
            );
            this.data = this.data.replace(oldKeywordRegex, newKeyword);
        }

        return this;
    }

    /**
     * the escape for RegExp function.
     * @param string the string to escape
     * @returns the escaped string
     */
    private escapeRegExp(string) {
        if (string && string.length) {
            return string.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&');
        }

        return string;
    }

    /**
     * the check if dependency exists function.
     * @param newKeyword the new keyword to check
     * @returns the boolean value
     */
    private checkIfKeywordExists(newKeyword: string): boolean {
        const regex = new RegExp(this.escapeRegExp(newKeyword), 'g');
        return regex.exec(this.data) ? true : false;
    }

    /**
     * removes the next n items after the index of the string containing the keyword
     * @param array the file content array
     * @param keyword the keyword, removeItem.key
     */
    private removeByKeyword(array: string[], keyword = '', removeIndex = 1, toBeRemovedAfter = undefined) {
        let startIndex = -1;
        const indent = spaceGenerator(4);
        if (toBeRemovedAfter) {
            startIndex = array.findIndex((arrayItem) => arrayItem.includes(toBeRemovedAfter));
            // If the marker for toBeRemovedAfter is not found, return the original array
            // No-op
            if (startIndex === -1) {
                return array;
            }
        }
        const index = array.findIndex((arrayItem, i) => {
            if (arrayItem.includes(keyword)) {
                if (i < startIndex) {
                    return false;
                } else {
                    return true;
                }
            }
            return false;
        });

        if (index !== -1) {
            if (index < array.length - removeIndex) {
                array.splice(index, removeIndex);
                Logger.log(`Removed the next ${removeIndex} items after index ${index}.`, indent);
            } else {
                Logger.log(`No ${removeIndex} items to remove after index ${index}.`, indent);
            }
        } else {
            Logger.log(`No string containing '${keyword}' was found.`, indent);
        }

        return array;
    }

    private getIndentByKeyword(input: string, target: string): number {
        const lines = input.split(/\r?\n/);
        let leadingSpaces = 0;

        for (let line of lines) {
            if (line.includes(target)) {
                for (let char of line) {
                    if (char === ' ') {
                        leadingSpaces++;
                    } else {
                        break;
                    }
                }
                Logger.log(`There are ${leadingSpaces} spaces before "${target}" on this line.`);
                break;
            }
        }

        return leadingSpaces;
    }

    private importFixer(imports: string): void {
        if (!this.data.includes(imports)) {
            this.data = imports + '\n' + this.data;
        }
    }
}
